import { Form, LineType, ResultType } from '../types';

export interface Result<T extends ResultType> {
  readonly type: T;
}

export interface ChallengeResult extends Result<ResultType.Challenge> {
  readonly flow?: Flow;
  form: Form;
}

export interface ErrorResult extends Result<ResultType.Error> {
  message?: string;
  ip?: string;
}

export type SuccessResult = Result<ResultType.Success>;

export type PassResult = Result<
  ResultType.Pass | ResultType.PartiallyImplementedNonFailure
>;

export interface FailureResult extends Result<ResultType.Failure> {
  message: string;
}

export interface RetryResult extends Result<ResultType.Retry> {
  message: string;
}

export type KeepAliveResult = SuccessResult | ErrorResult | FailureResult;

export type ApiResult =
  | PassResult
  | FailureResult
  | ChallengeResult
  | SuccessResult
  | RetryResult;

/** Returned from a pigeon validation attempt */
export interface ApiResponse {
  /** The result of the validation attempt */
  result: ApiResult;
  /** The id for this attempt, pass back to refer to the same request */
  loginAttemptId: string;
}

export enum Flow {
  /** The first auth flow for sending username/password */
  Initial = 'login',
  /** Flow that prompts client to choose method of mfa (pick phone number or email usually) */
  MfaSelect = 'mfaSelect',
  /** Flow that prompts client to enter the code sent to them for mfa */
  MfaCodeEntry = 'mfaCodeEntry',
  /** Flow that prompts client to choose which security question to answer */
  SecurityQuestionSelect = 'securityQuestionSelect',
  /** Flow that prompts client to answer the chosen security question */
  SecurityQuestionAnswer = 'securityQuestionAnswer',
  KeepAlive = 'keepAlive',
  /** Flow shown after login succeeds while documents are being downloaded (MFA may be required) */
  DocumentDownload = 'documentDownload',
}

/** The flow that that is ran in a validation attempt */
export type AuthFlow = Flow | string;

/** Enum of every websocket message that pigeon handles */
export enum WebsocketMessageType {
  /** DEPRECATED, used to be used to tell pigeon that the websocket is ready */
  Initialized = 'initialized',
  /** Start a validation attempt */
  Message = 'message',
  /** Used to listen to event about a subscriptions */
  SubmissionSubscription = 'submission_subscription',
  /** Sent once a submission is finished */
  SubmissionFinished = 'submission_finished',

  GetOrCreateValidationState = 'get_or_create_validation_state',

  GetValidationState = 'get_validation_state',
  CreateValidationState = 'create_validation_state',
  CarrierForm = 'carrier_form',

  /** Sent by the client when they fill out form */
  FormData = 'form-data',
  /** Validation data sent to the client */
  ValidationData = 'validation_data',
  /** Validation expired */
  ValidationExpired = 'validation_expired',
  UnsubscribeToSubmission = 'unsubscribe_to_submission',

  /* Removes unneeded validation state */
  AbortValidation = 'abort_validation',
  DemoValidationSuccess = 'demo_validation_success',
  /** Parrot asks pigeon to parse manually-uploaded declaration pages */
  TriggerManualParse = 'trigger_manual_parse',
  /** INS-1391: capability-checked, acknowledged form command (replaces form-data) */
  ValidationCommand = 'validation_command',
}

/** INS-1391: Socket.IO handshake `auth.protocolVersion`; Pigeon rejects anything else. */
export const PIGEON_PROTOCOL_VERSION = 2;
export const CLIENT_UPGRADE_REQUIRED = 'client_upgrade_required';

export class WebsocketMessageTypeFactory {
  static submissionData(submissionId: string) {
    return `submission_data_${submissionId}`;
  }
}

export interface AuthFlowWebsocketMessage {
  flow: AuthFlow;
  data: Record<string, string | undefined> | CarrierLoginData;
  carrierSlug: string;
  submissionId: string;
  loginAttemptId?: string;
  clientId: string;
  lineType: LineType;
}

export interface CarrierLoginData {
  username: string;
  password: string;
  zip?: string;
  state?: string;
}

export interface ValidationAttemptWebsocketMessage {
  carrierSlug: string;
  submissionId: string;
  clientId: string;
  loginData: CarrierLoginData;
}

export interface InitialLoginData {
  clientId: string;
  carrierSlug: string;
  lineType: LineType;
  submissionId?: string;
}

export interface MfaCodeWebsocketMessage {
  loginAttemptId: string;
  carrierSlug: string;
  submissionId: string;
  clientId: string;
  formName: string;
  formValue: string;
  flow: string;
}

export type StepResult =
  | PassResult
  | ErrorResult
  | FailureResult
  | ChallengeResult
  | SuccessResult
  | RetryResult
  | undefined;

export interface TestResult {
  credentialType: string;
  carrierSlug: string;
  submissionId: string;
  result: string;
  duration: number;
  testRun: string;
}

export interface CanaryResults {
  passedResults: TestResult[][];
  failedResults: TestResult[][];
}

export interface ComparionAgent {
  name: string;
  jobTitle: string;
  phoneNumber: string;
  email?: string;
  ratings: string;
  numberOfReviews: string;
}

export interface ValidationLoggingMetadata {
  attempt: number;
  carrierSlug: string;
  submissionId: string;
  flow: Flow;
}

/** Browserbase session interface */
export interface BrowserbaseSession {
  id: string;
  connectUrl: string;
  status: 'running' | 'stopped' | 'error';
  createdAt: string;
  expiresAt: string;
}

/** Browserbase session status */
export interface BrowserbaseSessionStatus {
  status: 'running' | 'stopped' | 'error';
  connectUrl?: string;
}

export interface ManualParsePage {
  decPageUuid: string;
  fileUrl: string;
  fileType: string;
  /** True if the dec page already has a policy_id (already parsed) — pigeon skips it. */
  alreadyLinked?: boolean;
}

export interface TriggerManualParseWsMessage {
  clientId: string;
  submissionId: string;
  carrierSlug: string;
  agentId?: number;
  pages: ManualParsePage[];
  /** Policy ids already present on the submission — pigeon skips creating a duplicate policy for these. */
  existingPolicyIds?: string[];
}
